import * as THREE from 'three';

export interface FocusMarker {
  /** Show the marker at a world point. Fades in, holds, then fades out. */
  showAt: (point: THREE.Vector3, now: number) => void;
  /** Per-frame update: orient toward camera + advance the fade tween. */
  tick: (now: number) => void;
  dispose: () => void;
}

// Ring size scales with camera distance so screen-size is constant.
// outer-radius / camera-distance ≈ 2*ratio/tan(fov/2) of vertical screen
// height. With ratio 0.028 and fov 50° → ~6% of vertical screen — readable
// but not obtrusive.
const RING_TO_DIST_RATIO = 0.028;
// Inner radius as fraction of outer — a thin clean stroke that reads as a
// "ring" (clearly hollow), not a "disk".
const INNER_RADIUS_FRACTION = 0.88;
// Timing matches camera fly-to (tour.defaults.cameraFlyDurationMs = 1400ms)
// so the marker stays at full opacity during the entire flight.
const FADE_IN_MS = 180;
const HOLD_MS = 1100;
const FADE_OUT_MS = 500;

export function createFocusMarker(scene: THREE.Scene, camera: THREE.Camera): FocusMarker {
  const geometry = new THREE.RingGeometry(INNER_RADIUS_FRACTION, 1, 96);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide
  });
  const ring = new THREE.Mesh(geometry, material);
  ring.renderOrder = 9999;
  ring.visible = false;
  scene.add(ring);

  const point = new THREE.Vector3();
  const camWorld = new THREE.Vector3();
  let shownAt: number | null = null;

  return {
    showAt(p: THREE.Vector3, now: number) {
      point.copy(p);
      shownAt = now;
      ring.position.copy(p);
      ring.visible = true;
    },
    tick(now: number) {
      if (shownAt === null) return;
      const elapsed = now - shownAt;
      const total = FADE_IN_MS + HOLD_MS + FADE_OUT_MS;
      if (elapsed >= total) {
        ring.visible = false;
        shownAt = null;
        return;
      }
      let t: number;
      if (elapsed < FADE_IN_MS) {
        t = elapsed / FADE_IN_MS;
      } else if (elapsed < FADE_IN_MS + HOLD_MS) {
        t = 1;
      } else {
        t = 1 - (elapsed - FADE_IN_MS - HOLD_MS) / FADE_OUT_MS;
      }
      material.opacity = t;

      camera.getWorldPosition(camWorld);
      ring.lookAt(camWorld);
      const dist = camWorld.distanceTo(point);
      ring.scale.setScalar(dist * RING_TO_DIST_RATIO);
    },
    dispose() {
      scene.remove(ring);
      geometry.dispose();
      material.dispose();
    }
  };
}
